import React, { useEffect, useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Sparkles, X, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * iter-121.154 / iter-121.186 — "Load sample data" industry picker with a
 * real progress bar.
 *
 * Industries are fetched live from the connector catalog
 * (`/auth/signup/sample-industries`). Picking one POSTs to
 * `/auth/signup/load-sample-industry`, which now runs the per-connector
 * ingest in a BACKGROUND job and returns a `job_id`. We then poll
 * `/auth/signup/load-sample-industry/status/{job_id}` to drive a live
 * progress bar + per-connector status until the dashboard is built.
 */
export default function IndustrySampleDialog({ open, onOpenChange, onLoaded }) {
    const [industries, setIndustries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null); // {status,total,completed,connectors:[]}
    const pollRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/auth/signup/sample-industries")
            .then((r) => {
                const items = r.data?.industries || [];
                setIndustries(items);
                setSelected((prev) => prev || (items[0]?.industry ?? null));
            })
            .catch(() => setIndustries([]))
            .finally(() => setLoading(false));
    }, [open]);

    // Cleanup poller on unmount / close.
    useEffect(() => {
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, []);

    useEffect(() => {
        if (!open) {
            if (pollRef.current) clearTimeout(pollRef.current);
            setProgress(null);
            setBusy(false);
        }
    }, [open]);

    const finish = (job) => {
        const connectors = job.connectors_result || [];
        const n = connectors.length;
        if (n > 0) {
            toast.success("Sample data loaded", {
                description: `${n} ${selected} source${n !== 1 ? "s" : ""} ready — opening your dashboard.`,
            });
            onLoaded?.({
                connectors,
                dashboard_id: job.dashboard_id,
                dataset_ids: job.dataset_ids || [],
                failed: job.failed || [],
            });
            onOpenChange?.(false);
        } else {
            toast.error("Could not load sample data for this industry.");
            setBusy(false);
            setProgress(null);
        }
    };

    const poll = async (jobId) => {
        try {
            const r = await api.get(`/auth/signup/load-sample-industry/status/${jobId}`);
            const job = r.data || {};
            setProgress(job);
            if (job.status === "done" || job.status === "error") {
                finish(job);
                return;
            }
        } catch {
            /* transient — keep polling */
        }
        pollRef.current = setTimeout(() => poll(jobId), 1500);
    };

    const load = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            const r = await api.post("/auth/signup/load-sample-industry", { industry: selected });
            const res = r.data || {};
            if (res.job_id) {
                setProgress(res);
                poll(res.job_id);
            } else {
                // Backward-compat: synchronous response (shouldn't happen).
                finish({ ...res, connectors_result: res.connectors || [] });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load sample data.");
            setBusy(false);
        }
    };

    const total = progress?.total || 0;
    const completed = progress?.completed || 0;
    const pct = total ? Math.min(100, Math.round((completed / total) * 100)) : 0;

    const statusIcon = (st) => {
        if (st === "done") return <Check className="h-3.5 w-3.5 text-emerald-500" />;
        if (st === "failed") return <X className="h-3.5 w-3.5 text-destructive" />;
        if (st === "loading") return <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />;
        return <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />;
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
            <DialogContent
                className="max-w-lg"
                data-testid="industry-sample-dialog"
                onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
            >
                <DialogHeader>
                    <DialogTitle>{progress ? "Loading sample data" : "Load sample data"}</DialogTitle>
                    <DialogDescription>
                        {progress
                            ? "Pulling sample data from each source and building your first dashboard. This can take a minute."
                            : "Pick an industry. We'll pull sample data from every connector in it, then build your first dashboard."}
                    </DialogDescription>
                </DialogHeader>

                {progress ? (
                    /* ── Progress view ─────────────────────────────── */
                    <div className="py-2 space-y-4" data-testid="industry-progress">
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                                <span>{completed} of {total} sources</span>
                                <span data-testid="industry-progress-pct">{pct}%</span>
                            </div>
                            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                                <div
                                    className="h-full bg-primary transition-all duration-500 ease-out"
                                    style={{ width: `${pct}%` }}
                                    data-testid="industry-progress-bar"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5 max-h-[40vh] overflow-auto">
                            {(progress.connectors || []).map((c) => (
                                <div
                                    key={c.key}
                                    className="flex items-center gap-2.5 px-3 py-2 border border-border rounded-sm text-sm"
                                    data-testid={`industry-progress-row-${c.key}`}
                                >
                                    <span className="w-4 flex items-center justify-center">{statusIcon(c.status)}</span>
                                    <span className="truncate">{c.name}</span>
                                    <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                        {c.status === "loading" ? "loading" : c.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : loading ? (
                    <div className="py-10 flex items-center justify-center text-muted-foreground text-sm" data-testid="industry-loading">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading industries…
                    </div>
                ) : industries.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground" data-testid="industry-empty">
                        No sample-ready industries are available yet.
                    </div>
                ) : (
                    <div className="space-y-3 max-h-[46vh] overflow-auto py-1">
                        {industries.map((ind) => {
                            const active = selected === ind.industry;
                            const count = ind.connectors?.length || 0;
                            return (
                                <button
                                    key={ind.industry}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => setSelected(ind.industry)}
                                    className={`w-full text-left p-4 border rounded-sm transition-colors ${
                                        active ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"
                                    }`}
                                    data-testid={`industry-option-${ind.industry.toLowerCase()}`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                                            active ? "border-primary bg-primary" : "border-muted-foreground"
                                        }`}>
                                            {active && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                                        </div>
                                        <span className="text-sm font-semibold">{ind.label}</span>
                                        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                            {count} connector{count !== 1 ? "s" : ""}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 pl-6">
                                        {(ind.connectors || []).map((c) => (
                                            <span
                                                key={c.key}
                                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border border-border rounded-sm bg-background"
                                            >
                                                {c.icon_url && (
                                                    <img
                                                        src={c.icon_url}
                                                        alt=""
                                                        className="h-3 w-3 object-contain"
                                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                    />
                                                )}
                                                {c.name}
                                            </span>
                                        ))}
                                    </div>
                                </button>
                            );
                        })}
                        <p className="text-[11px] text-muted-foreground">
                            Only industries with available connectors appear here. More show up automatically as new connectors are added.
                        </p>
                    </div>
                )}

                {!progress && (
                    <DialogFooter>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => onOpenChange?.(false)}
                            className="rounded-sm"
                            data-testid="industry-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            disabled={busy || !selected || industries.length === 0}
                            onClick={load}
                            className="rounded-sm"
                            data-testid="industry-load"
                        >
                            {busy ? (
                                <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Starting…</>
                            ) : (
                                <>Load <Sparkles className="h-3.5 w-3.5 ml-1.5" /></>
                            )}
                        </Button>
                    </DialogFooter>
                )}

                {progress && (progress.failed || []).length > 0 && progress.status === "done" && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                        Some sources couldn't load — your dashboard still opened with the rest.
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
